import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { currentRevisionOf, groupParts, nextRevisionName, partsChanged } from '@kronos/domain'
import type { AuthoredLine } from '@kronos/domain'
import { repository } from '../../data'
import type { ComponentType, Revision } from '../../data'
import type { Category } from '../../data/inventory'
import type { PreparedImage } from '../imageFile'
import { Modal } from '../kit'
import { AdvancedSection } from './editor/AdvancedSection'
import { BasicsSection } from './editor/BasicsSection'
import { BuyingSection } from './editor/BuyingSection'
import { EditorFooter, ForkedDone } from './editor/EditorFooter'
import { PartsSection } from './editor/PartsSection'
import { useItemSave } from './editor/useItemSave'
import type { SaveResult } from './editor/useItemSave'
import { blankDraft, draftOf, hasParts, hasProblems, previewType, problemsOf, shapeOf } from './itemDraft'
import { errorText } from './shared'

/** The id the preview tree uses for an item that does not exist yet. */
const NEW_ID = '__new_item__'

/**
 * New item and Edit item — one form, opened from Inventory, an item's page,
 * the tree's inspector and Receive. Engineers only; the server checks
 * `edit_catalog` on every write it makes.
 *
 * A wide sheet at a desk and the whole screen on a phone, with Save and
 * Cancel pinned to the bottom. Basics and Buying are what most items need;
 * Made of appears for things built here or by a vendor; everything else
 * waits behind Tracking details.
 */
export function ItemEditor({
  typeId,
  initialName = '',
  onClose,
  onSaved,
}: {
  /** Absent for a new item. */
  typeId?: string
  initialName?: string
  onClose: () => void
  onSaved: (id: string) => void
}): JSX.Element {
  const types = useQuery({ queryKey: ['component-types'], queryFn: () => repository.componentTypes() })
  const revisions = useQuery({ queryKey: ['revisions'], queryFn: () => repository.revisions() })
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => repository.categories() })
  const existing = typeId === undefined ? undefined : types.data?.find((t) => t.id === typeId)

  if (!types.data || !revisions.data || !categories.data || (typeId !== undefined && !existing)) {
    const failed = types.isError || revisions.isError || (types.data && typeId !== undefined && !existing)
    return (
      <Modal className="modal--sheet" title={typeId ? 'Edit item' : 'New item'} onClose={onClose}>
        <p className="muted">{failed ? 'Could not load this item. It may have been retired.' : 'Loading…'}</p>
      </Modal>
    )
  }
  return (
    <EditorForm
      existing={existing}
      initialName={initialName}
      types={types.data}
      revisions={revisions.data}
      categories={categories.data}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

function EditorForm({
  existing,
  initialName,
  types,
  revisions,
  categories,
  onClose,
  onSaved,
}: {
  existing: ComponentType | undefined
  initialName: string
  types: readonly ComponentType[]
  revisions: readonly Revision[]
  categories: readonly Category[]
  onClose: () => void
  onSaved: (id: string) => void
}): JSX.Element {
  // What it is made of today — the baseline a change is measured against.
  const [saved] = useState<readonly AuthoredLine[]>(() =>
    existing ? (currentRevisionOf(revisions, existing.id)?.lines ?? []).map((l) => ({ childTypeId: l.childTypeId, quantity: l.quantity, position: l.position })) : [],
  )
  const [initial] = useState(() => (existing ? draftOf(existing) : blankDraft(initialName)))
  const [draft, setDraft] = useState(initial)
  const [parts, setParts] = useState(() => groupParts(saved))
  const [staged, setStaged] = useState<PreparedImage | null>(null)
  const [attempted, setAttempted] = useState(false)
  const [done, setDone] = useState<(SaveResult & { readonly from: string | null }) | null>(null)
  const set = (patch: Partial<typeof draft>): void => setDraft((d) => ({ ...d, ...patch }))
  const save = useItemSave(existing, (category) => set({ category, newCategory: '' }))

  const source = useMemo(() => ({ types, revisions }), [types, revisions])
  const live = existing ? types.find((t) => t.id === existing.id) : undefined
  const partsDirty = partsChanged(saved, parts)
  const problems = problemsOf(draft, parts, types.filter((t) => t.id !== existing?.id).map((t) => t.name))
  // An empty name is only an error once somebody tried to save; a taken one is worth saying at once.
  const shown = attempted ? problems : { ...problems, name: draft.name.trim() === '' ? undefined : problems.name, newCategory: undefined }
  const lock = existing?.structure?.verdict ?? null
  const revision = {
    current: existing ? (currentRevisionOf(revisions, existing.id)?.name ?? null) : null,
    next: nextRevisionName(revisions.filter((r) => r.componentTypeId === existing?.id).map((r) => r.name)),
  }
  const willFork = hasParts(draft) && partsDirty && lock !== null && !lock.editable
  const dirty = partsDirty || staged !== null || JSON.stringify(draft) !== JSON.stringify(initial)

  // Built here, the parts list is the point and comes first; bought or vendor-made, the supplier is.
  const builtHere = shapeOf(draft).kind === 'make_internal'
  const buying = <BuyingSection draft={draft} set={set} problems={shown} />

  const close = (): void => {
    if (done === null && dirty && !save.isPending && !window.confirm('Discard your changes to this item?')) return
    onClose()
  }
  const submit = (): void => {
    setAttempted(true)
    if (hasProblems(problems)) return
    save.mutate({ draft, parts, partsDirty, staged }, { onSuccess: (r) => (r.structure?.frozen ? setDone({ ...r, from: revision.current }) : onSaved(r.id)) })
  }

  return (
    <Modal
      className="modal--sheet"
      title={existing ? `Edit ${existing.name}` : 'New item'}
      subtitle={existing ? 'Changes apply to every one of these.' : 'Something we buy, have made, or build here.'}
      onClose={close}
      footer={
        done ? (
          <button type="button" className="primary" onClick={() => onSaved(done.id)}>
            Done
          </button>
        ) : (
          <EditorFooter
            error={save.isError ? errorText(save.error) : null}
            blocked={attempted && hasProblems(problems)}
            forksTo={willFork ? revision.next : null}
            saving={save.isPending}
            label={!existing ? 'Add item' : willFork ? `Save as ${revision.next}` : 'Save changes'}
            onCancel={close}
            onSave={submit}
          />
        )
      }
    >
      {done ? (
        <ForkedDone from={done.from} to={done.structure?.revisionName ?? revision.next} />
      ) : (
        <div className="ie">
          <BasicsSection
            draft={draft}
            set={set}
            existing={existing}
            problems={shown}
            categories={categories}
            imageId={live?.imageId ?? null}
            staged={staged}
            onStage={setStaged}
          />
          {builtHere ? null : buying}
          {hasParts(draft) ? (
            <PartsSection
              parentId={existing?.id ?? null}
              parentName={draft.name}
              parts={parts}
              onParts={setParts}
              saved={saved}
              lock={lock}
              revision={revision}
              problems={shown}
              dirty={partsDirty}
              types={types}
              source={source}
              preview={previewType(draft, existing?.id ?? NEW_ID, existing)}
            />
          ) : null}
          {builtHere ? buying : null}
          <AdvancedSection draft={draft} set={set} existing={existing} />
        </div>
      )}
    </Modal>
  )
}
